import React, {useEffect, useState} from 'react';
import {Button, Input, message, Modal, Tooltip, Select, Drawer, DatePicker} from "antd";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import {EyeIcon, PencilSquareIcon, PlusIcon, TrashIcon} from "@heroicons/react/24/outline";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    StatusBadge,
} from "../../components/ui";

const {Option} = Select;

const statusTones = {
    process: 'blue',
    accept: 'green',
    cancel: 'red',
};

function Warehouse(props) {
    const { t } = useTranslation();
    const formatSum = (value) => `${new Intl.NumberFormat('ru-UZ').format(value)} ${t('common.currency')}`;
    const [requests, setRequests] = useState([]);
    const [categories, setCategories] = useState([]); // State to hold categories
    const [isOpen, setIsOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [selectedQty, setSelectedQty] = useState(null);
    const [data, setData] = useState({
        category_id: null,
        name: '',
        qty: '',
        price: ''
    });
    const [isHistoryDrawerVisible, setIsHistoryDrawerVisible] = useState(false); // For drawer visibility
    const [selectedHistory, setSelectedHistory] = useState([]); // Store selected item's history
    const [selectedCategory, setSelectedCategory] = useState(null); // Track selected category for filtering
    const [name, setName] = useState('')
    const [date, setDate] = useState(null);
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    const openHistoryDrawer = (histories, qty) => {
        setSelectedHistory(histories);
        setSelectedQty(qty);
        setIsHistoryDrawerVisible(true);
    };

    const closeHistoryDrawer = () => {
        setIsHistoryDrawerVisible(false); // Close the drawer
    };

    const fetchRequests = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                category_id: selectedCategory || undefined,
                name: name || undefined,
                date: date ? date.format('YYYY-MM-DD') : undefined, // Sana formatini to'g'ri berish
            };
            const response = await axiosInstance.get('/financiers/warehouses', {params});
            const {data, meta} = response.data;
            setRequests(data);
            setPagination({
                current: meta.current_page,
                pageSize: meta.per_page,
                total: meta.total,
            });
        } catch (error) {
            console.error('Error fetching requests:', error);
            message.error(t('store.loadError'));
        } finally {
            setLoading(false);
        }
    };


    // Fetch the list of categories
    const fetchCategories = async () => {
        try {
            const response = await axiosInstance.get('/financiers/categories');
            setCategories(response.data.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
            message.error(t('store.fetchCategoriesError'));
        }
    };

    useEffect(() => {
        fetchRequests(pagination.current);
        fetchCategories(); // Fetch categories when component loads
    }, [selectedCategory]);

    const handleTableChange = (pagination) => {
        fetchRequests(pagination.current);
    };

    const handleCategoryChange = (value) => {
        setSelectedCategory(value);
    };

    const openModal = (mode, request = null) => {
        setModalMode(mode);
        if (mode === 'edit' && request) {
            setSelectedRequest(request);

            setData({
                category_id: request.category.id,
                name: request.name,
                qty: request.qty,
                price: request.price
            });
        } else {
            setData({category_id: null, name: '', qty: '', price: ''});
        }
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setSelectedRequest(null);
        setData({category_id: null, name: '', qty: '', price: ''});
    };

    // POST request to add a new store request
    const addRequest = async () => {
        try {
            const response = await axiosInstance.post('/financiers/warehouses', data);
            setRequests([...requests, response.data.data]);
            message.success(t('store.addedOk'));
            closeModal();
        } catch (error) {
            console.error('Error adding request:', error);
            message.error(t('store.addError'));
        }
    };

    // PUT request to update an existing store request
    const updateRequest = async () => {
        try {
            const response = await axiosInstance.put(`/financiers/warehouses/${selectedRequest.id}`, data);
            setRequests(requests.map((req) => (req.id === selectedRequest.id ? response.data.data : req)));
            message.success(t('store.productUpdated'));
            closeModal();
        } catch (error) {
            console.error('Error updating request:', error);
            message.error(t('store.updateError'));
        }
    };

    // DELETE request to delete an existing store request
    const deleteRequest = async (requestId) => {
        try {
            await axiosInstance.delete(`/financiers/warehouses/${requestId}`);
            setRequests(requests.filter((req) => req.id !== requestId));
            message.success(t('store.requestDeleted'));
        } catch (error) {
            console.error('Error deleting request:', error);
            message.error(t('store.requestDeleteError'));
        }
    };

    // Logic to either add or update the request based on modal mode
    const handleOk = async () => {
        if (!data.category_id || !data.name) {
            message.error(t('store.fillFields'));
            return;
        }
        if (modalMode === 'add') {
            await addRequest();
        } else {
            await updateRequest();
        }
    };

    const historyButton = (record) => (
        <button
            type="button"
            onClick={() => openHistoryDrawer(record.histories, record.qty)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-[#1D2C7B] hover:bg-slate-50 transition-colors"
        >
            <EyeIcon className="w-4 h-4"/>
            {t('store.view')}
        </button>
    );

    const rowActions = (record) => (
        <div className="flex items-center gap-0.5">
            <Tooltip title={t('common.edit')}>
                <RowIconButton onClick={() => openModal('edit', record)}>
                    <PencilSquareIcon className="w-[18px] h-[18px]"/>
                </RowIconButton>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
                <RowIconButton className="hover:!text-red-600" onClick={() => deleteRequest(record.id)}>
                    <TrashIcon className="w-[18px] h-[18px]"/>
                </RowIconButton>
            </Tooltip>
        </div>
    );

    const columns = [
        {
            title: t('common.id'),
            dataIndex: 'id',
            key: 'id',
            width: 72,
            render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>
        },
        {
            title: t('common.category'),
            dataIndex: 'category',
            key: 'category_id',
            render: (category) => {
                return <span className="text-sm text-slate-700">{category.name}</span>;
            }
        },
        {
            title: t('store.name'),
            dataIndex: 'name',
            key: 'name',
            render: (text) => <span className="text-sm font-medium text-[#1D2C7B]">{text}</span>,
        },
        {
            title: t('common.qty'),
            dataIndex: 'qty',
            width: 96,
            key: 'qty',
            render: (qty) => <span className="text-sm text-slate-700 tabular-nums">{qty}</span>,
        },
        {
            title: t('common.price'),
            dataIndex: 'price',
            width: 140,
            key: 'price',
            render: (text, record) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{formatSum(record.price)}</span>
            )
        },
        {
            title: t('store.grandTotal'),
            width: 150,
            render: (text, record) => (
                <span className="text-sm font-medium text-slate-800 tabular-nums whitespace-nowrap">
                    {formatSum(record.qty * record.price)}
                </span>
            )
        },

        {
            title: t('store.requestHistory'),
            width: 140,
            key: 'histories',
            render: (_, record) => historyButton(record)
        },
        {
            title: t('common.actions'),
            key: 'action',
            width: 96,
            render: (text, record) => rowActions(record),
        },
    ];

    const renderStatusTag = (status) => {
        const label = t(`status.${status}`, { defaultValue: t('common.unknown') });

        return <StatusBadge tone={statusTones[status] ?? 'muted'}>{label}</StatusBadge>;
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
    };

    const handleDateChange = (date) => {
        setDate(date);
    };

    const handleExport = async () => {
        try {
          const params = {
            category_id: selectedCategory || undefined,
            name: name || undefined,
            date: date ? date.format('YYYY-MM-DD') : undefined,
          };
      
          const response = await axiosInstance.get('/financiers/warehouse/excel/export', {
            params,
            responseType: 'blob', // важно для файлов
          });
      
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'warehouse_export.xlsx'); // Имя файла
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      
          message.success(t('store.exportOk'));
        } catch (error) {
          console.error('Export error:', error);
          message.error(t('store.exportError'));
        }
      };


    // Drawer columns for histories
    const historyColumns = [
        {
            title: t('common.id'),
            dataIndex: 'id',
            key: 'id',
            width: 72,
            render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
        },
        {
            title: t('common.qty'),
            dataIndex: 'qty',
            key: 'qty',
            width: 96,
            render: (qty) => <div className={'flex items-center text-sm text-slate-700 tabular-nums'}>-{qty}</div>
        },
        {
            title: t('common.status'),
            dataIndex: 'status',
            key: 'status',
            width: 170,
            render: (status) => renderStatusTag(status)
        },
        {
            title: t('common.date'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 170,
            render: (value) => <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{value}</span>,
        },
        {
            title: t('store.sender'),
            dataIndex: 'sender',
            key: 'sender',
            render: (sender) => <span className="text-sm font-medium text-[#1D2C7B]">{sender.name}</span>,
        },
        {
            title: t('store.receiver'),
            dataIndex: 'receive',
            key: 'receive',
            render: (receive) => receive
                ? <span className="text-sm font-medium text-[#1D2C7B]">{receive.name}</span>
                : <span className="text-xs text-slate-500">{t('store.notReceived')}</span>,
        },
        {
            title: t('common.remark'),
            dataIndex: 'history',
            key: 'comment',
            render: (history) => history?.comment
                ? <p className="capitalize m-0 text-sm" style={{color: '#1D2C7B'}}>{history?.comment}</p>
                : <span className="text-xs text-slate-500">{t('store.noRemark')}</span>,
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                    <p className="m-0 text-sm font-semibold text-[#1D2C7B] break-words">{record.name}</p>
                    <span className="block text-xs text-slate-500 mt-0.5">{record.category?.name}</span>
                </div>
                {rowActions(record)}
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                <span className="tabular-nums">{record.qty}</span>
                <span className="tabular-nums">{formatSum(record.price)}</span>
                <span className="font-semibold text-slate-800 tabular-nums">{formatSum(record.qty * record.price)}</span>
            </div>

            <div className="mt-2.5">{historyButton(record)}</div>
        </div>
    );

    const renderHistoryMobileCard = (record) => (
        <div>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="m-0 text-sm font-semibold text-[#1D2C7B] break-words">{record.sender?.name}</p>
                    <span className="block text-xs text-slate-500 tabular-nums mt-0.5">{record.created_at}</span>
                </div>
                <span className="text-sm font-semibold text-slate-800 tabular-nums shrink-0">-{record.qty}</span>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                {renderStatusTag(record.status)}
                <span>{record.receive ? record.receive.name : t('store.notReceived')}</span>
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('store.warehouse')}
                    count={pagination.total}
                    filters={
                        <>
                            {/* Номи бўйича фильтр */}
                            <Input
                                size="small"
                                placeholder={t('store.nameFilter')}
                                value={name}
                                onChange={handleNameChange}
                                className="md:!w-[180px]"
                            />

                            {/* Категория бўйича фильтр */}
                            <Select
                                size="small"
                                className="md:min-w-[170px]"
                                placeholder={t('store.pickCategory')}
                                onChange={handleCategoryChange}
                                allowClear
                            >
                                {categories.map((category) => (
                                    <Option key={category.id} value={category.id}>
                                        {category.name}
                                    </Option>
                                ))}
                            </Select>

                            {/* Сана бўйича фильтр */}
                            <DatePicker
                                size="small"
                                format={'YYYY-MM-DD'}
                                value={date}
                                onChange={handleDateChange}
                                className="md:!w-[140px]"
                            />

                            {/* Фильтрлаш тугмаси */}
                            <Button
                                size="small"
                                type="primary"
                                style={{backgroundColor: '#1D2C7B', borderColor: '#1D2C7B'}}
                                onClick={() => fetchRequests(1)}
                            >
                                {t('common.filter')}
                            </Button>
                        </>
                    }
                    actions={
                        <>
                            <ActionButton icon={<PlusIcon className="w-4 h-4"/>} onClick={() => openModal('add')}>
                                {t('store.addNew')}
                            </ActionButton>

                            <Button
                                type="default"
                                onClick={handleExport}
                                className="!h-9 !rounded-lg !text-sm !font-medium"
                            >
                                {t('store.excelExport')}
                            </Button>
                        </>
                    }
                />

                <DataTable
                    loading={loading}
                    columns={columns}
                    dataSource={requests}
                    rowKey="id"
                    scrollX={1100}
                    mobileCard={renderMobileCard}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                    onChange={handleTableChange}/>
            </PageCard>

            {/* Drawer for showing history */}
            <Drawer
                title={t('store.historyQty', { qty: selectedQty || 0 })}
                placement="bottom"
                height="85%"
                onClose={closeHistoryDrawer}
                open={isHistoryDrawerVisible}
                styles={{body: {padding: 0}}}
            >
                <DataTable
                    columns={historyColumns}
                    dataSource={selectedHistory}
                    rowKey="id"
                    scrollX={1100}
                    mobileCard={renderHistoryMobileCard}
                    pagination={false}/>
            </Drawer>

            <Modal
                title={modalMode === 'add' ? t('store.addNew') : t('store.edit')}
                open={isOpen}
                onCancel={closeModal}
                onOk={handleOk}
                okText={modalMode === 'add' ? t('store.addNew') : t('store.edit')}
                width="100%"
                style={{maxWidth: 520}}
            >
                <Select
                    placeholder={t('store.categoryPh')}
                    value={data.category_id}
                    onChange={(value) => setData({...data, category_id: value})}
                    style={{width: '100%', marginBottom: 16}}
                >
                    {categories.map((category) => (
                        <Select.Option key={category.id} value={category.id}>
                            {category.name}
                        </Select.Option>
                    ))}
                </Select>
                <Input
                    placeholder={t('store.namePh')}
                    value={data.name}
                    onChange={(e) => setData({...data, name: e.target.value})}
                    style={{marginBottom: 16}}
                />
                <Input
                    placeholder={t('store.qtyPh')}
                    value={data.qty}
                    onChange={(e) => setData({...data, qty: e.target.value})}
                    style={{marginBottom: 16}}
                />
                <Input
                    placeholder={t('store.pricePh')}
                    value={data.price}
                    onChange={(e) => setData({...data, price: e.target.value})}
                    style={{marginBottom: 16}}
                />
            </Modal>
        </PageShell>
    );
}

export default Warehouse;
